import { DataService } from "@/services/data";
import {
    VaultAccessLogType,
    VaultRevealResponse,
    VaultSessionStatus,
    VaultTotpSetupResponse,
    VaultTotpStatus,
    VaultUnlockResponse,
} from "@/types/tools/vault_credential";

const VAULT_TOKEN_KEY = "vault_session_token";
const BASE = "/crm/objects/vault-credentials";

export const VaultService = {
    getToken(): string | null {
        return sessionStorage.getItem(VAULT_TOKEN_KEY);
    },

    setToken(token: string | null) {
        if (token) sessionStorage.setItem(VAULT_TOKEN_KEY, token);
        else sessionStorage.removeItem(VAULT_TOKEN_KEY);
    },

    withVaultHeaders() {
        const token = this.getToken();
        const headers: Record<string, string> = {};
        if (token) headers["X-Vault-Session"] = token;
        return DataService.client.headers(headers);
    },

    async getSession(): Promise<VaultSessionStatus> {
        return this.withVaultHeaders().url(`${BASE}/vault/session/`).get().json();
    },

    async unlock(code: string): Promise<VaultUnlockResponse> {
        const res = (await DataService.client.url(`${BASE}/vault/unlock/`).post({ code }).json()) as VaultUnlockResponse;
        this.setToken(res.token);
        return res;
    },

    async lock(): Promise<void> {
        await this.withVaultHeaders().url(`${BASE}/vault/lock/`).post({}).res();
        this.setToken(null);
    },

    async totpStatus(): Promise<VaultTotpStatus> {
        return DataService.client.url(`${BASE}/totp/status/`).get().json();
    },

    async totpSetup(): Promise<VaultTotpSetupResponse> {
        return DataService.client.url(`${BASE}/totp/setup/`).post({}).json();
    },

    async totpConfirm(code: string): Promise<{ enabled: boolean }> {
        return DataService.client.url(`${BASE}/totp/confirm/`).post({ code }).json();
    },

    async reveal(id: number): Promise<VaultRevealResponse> {
        return this.withVaultHeaders().url(`${BASE}/${id}/reveal/`).get().json();
    },

    async copy(id: number): Promise<void> {
        await this.withVaultHeaders().url(`${BASE}/${id}/copy/`).post({}).res();
    },

    async listAccessLogs(params?: { action?: string; user_id?: number; credential_id?: number }): Promise<VaultAccessLogType[]> {
        const qs = new URLSearchParams();
        if (params?.action) qs.set("action", params.action);
        if (params?.user_id) qs.set("user_id", String(params.user_id));
        if (params?.credential_id) qs.set("credential_id", String(params.credential_id));
        const suffix = qs.toString() ? `?${qs.toString()}` : "";
        return DataService.client.url(`/crm/app/vault-access-logs/${suffix}`).get().json();
    },
};
